import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtTRY } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { TrendingUp, Wallet, AlertTriangle, Users, ArrowUpRight, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const Metric = ({ label, value, sub, icon: Icon, tone, testid }) => (
  <div data-testid={testid} className="bg-zinc-950 border border-zinc-800 p-5 flex flex-col">
    <div className="flex items-center justify-between mb-3">
      <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-medium">{label}</span>
      <Icon className={`w-4 h-4 ${tone || "text-zinc-500"}`} />
    </div>
    <span className="text-3xl font-mono font-semibold tracking-tight">{value}</span>
    {sub && <span className="text-xs text-zinc-500 mt-1.5">{sub}</span>}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [reminders, setReminders] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/dashboard/monthly").then((r) => setMonthly(r.data));
    api.get("/dashboard/reminders").then((r) => setReminders(r.data));
  }, []);

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Özet</h1>
        <p className="text-zinc-500 text-sm mt-1">Ofis finans ve müşteri durumu genel görünümü</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <Metric testid="stat-toplam-alacak" label="Toplam Alacak" value={fmtTRY(stats?.toplam_alacak)} sub="Müşterilerden tahsil edilecek" icon={Wallet} tone="text-yellow-400" />
        <Metric testid="stat-bu-ay-tahsilat" label="Bu Ay Tahsilat" value={fmtTRY(stats?.bu_ay_tahsilat)} sub="Cari döneme ait" icon={TrendingUp} tone="text-green-400" />
        <Metric testid="stat-gecikmis" label="Gecikmiş Ödemeler" value={stats?.gecikmis_sayi ?? 0} sub={fmtTRY(stats?.gecikmis_tutar)} icon={AlertTriangle} tone="text-red-400" />
        <Metric testid="stat-aktif-musteri" label="Aktif Müşteri" value={stats?.aktif_musteri ?? 0} sub={`Toplam ${stats?.toplam_musteri ?? 0} müşteri`} icon={Users} tone="text-blue-400" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-zinc-950 border border-zinc-800 p-5">
          <h2 className="text-base font-semibold mb-1">Aylık Tahakkuk / Tahsilat</h2>
          <p className="text-xs text-zinc-500 mb-5">Son 6 ay (₺)</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={monthly} barGap={6}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
              <XAxis dataKey="ay" stroke="#71717a" fontSize={12} tickLine={false} axisLine={{ stroke: "#27272a" }} />
              <YAxis stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} width={70}
                tickFormatter={(v) => new Intl.NumberFormat("tr-TR", { notation: "compact" }).format(v)} />
              <Tooltip
                contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 0, color: "#fff" }}
                formatter={(v) => fmtTRY(v)}
                cursor={{ fill: "#ffffff08" }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="tahakkuk" name="Tahakkuk" fill="#3f3f46" />
              <Bar dataKey="tahsilat" name="Tahsilat" fill="#22c55e" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-zinc-950 border border-zinc-800 flex flex-col">
          <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2">
            <Clock className="w-4 h-4 text-yellow-400" />
            <h2 className="text-base font-semibold">Ödeme Hatırlatmaları</h2>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[340px]" data-testid="reminders-list">
            {reminders.length === 0 && (
              <div className="p-6 text-center text-sm text-zinc-500">Bekleyen ödeme yok.</div>
            )}
            {reminders.map((r) => (
              <button
                key={r.customer_id}
                data-testid={`reminder-${r.customer_id}`}
                onClick={() => navigate(`/musteriler/${r.customer_id}`)}
                className="w-full text-left px-5 py-3.5 border-b border-zinc-900 hover:bg-zinc-900 transition-colors flex items-center justify-between group"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{r.unvan || "İsimsiz"}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Ayın {r.tahsilat_odeme_gunu}. günü</div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <div className="font-mono text-sm">{fmtTRY(r.bakiye)}</div>
                  <Badge className={`rounded-none text-[10px] uppercase tracking-wider mt-1 ${r.durum === "gecikti" ? "bg-red-500/15 text-red-400 border-red-500/30" : "bg-yellow-500/15 text-yellow-400 border-yellow-500/30"}`}>
                    {r.durum === "gecikti" ? "Gecikti" : "Yaklaşan"}
                  </Badge>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
